<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';

	//Stores

	//Svelte hooks
	import { onMount } from 'svelte';

	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_local_ip,
		getSettingUiComponent,
		isNoRelayPlug,
		real_id,
	} from '../../../../helpers/interface_helpers';

	//Settings
	import type { SettingRequest } from '../../../../types/requests/settings/setting';

	//Design system components
	import { Button, ColorWheel, Radio, Slider, SliderV2, Text } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device;

	const hasRelay = !isNoRelayPlug(device.type);
	const id = real_id(device.id),
		settings = readDeviceSettings(id),
		min = 0,
		max = 100,
		modes = ['power', 'switch', 'off'];
	const plugUi = getSettingUiComponent(device);
	const structure =
		device.type === 'S4PL-00416EU' //if power strip g4 -> all channels are sending prop 'switch:0'
			? 'switch:0'
			: get_gen_2_component_channel_structure(device); //this is the structure for the plug coming after https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2520

	let loading: boolean = false,
		brightnessValue: number = settings?.[plugUi]?.leds?.colors?.power?.brightness || 0,
		colorOn: number[] = settings[plugUi]?.leds?.colors?.[structure]?.on?.rgb?.map((c) =>
			Math.round(c * 2.55),
		),
		colorOff: number[] = settings[plugUi]?.leds?.colors?.[structure]?.off?.rgb?.map((c) =>
			Math.round(c * 2.55),
		),
		brightnessOn: number = settings[plugUi]?.leds?.colors?.[structure]?.on?.brightness,
		brightnessOff: number = settings[plugUi]?.leds?.colors?.[structure]?.off?.brightness,
		selectedMode: string = settings[plugUi].leds.mode,
		cacheMode: string = selectedMode,
		cacheBrightness: number = brightnessValue,
		cacheBrightnessOn: number = brightnessOn,
		cacheBrightnessOff: number = brightnessOff,
		cacheColorOn: number[] = colorOn,
		cacheColorOff: number[] = colorOff,
		local_ip: string,
		showModal: boolean = false;

	$: selectedIndex = modes.indexOf(selectedMode);
	$: visualModes = hasRelay ? modes : ['power', 'color', 'off']; //adding this because we need to show color as a mode but not send it to the device

	$: enabled =
		cacheMode !== selectedMode ||
		cacheBrightness !== brightnessValue ||
		cacheBrightnessOn !== brightnessOn ||
		cacheBrightnessOff !== brightnessOff ||
		(colorOn && JSON.stringify(cacheColorOn) !== JSON.stringify(colorOn)) ||
		(colorOff && JSON.stringify(cacheColorOff) !== JSON.stringify(colorOff)); //this change is coming because they removed off from the plug pm https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2520
	onMount(() => {
		local_ip = get_local_ip(id);
	});

	const setLedIndicator = async () => {
		if (enabled) {
			let colors = {};
			switch (selectedMode) {
				case 'power':
					colors = {
						power: {
							brightness: brightnessValue,
						},
					};
					break;
				case 'switch':
				case 'color':
					colors = {
						[structure]: {
							on: {
								rgb: colorOn.map((c) => Math.round(c / 2.55)),
								brightness: Number(brightnessOn),
							},
							off:
								(brightnessOff !== null || brightnessOff !== undefined) &&
								!isNoRelayPlug(device.type)
									? {
											rgb: colorOff.map((c) => Math.round(c / 2.55)),
											brightness: Number(brightnessOff),
										}
									: null,
						},
					};
					break;
				default:
					break;
			}
			const params = {
				id: device.channel,
				config: {
					leds: {
						colors,
						mode: selectedMode,
					},
				},
			};

			loading = true;
			const res: SettingRequest = await gen2Request(device, `${plugUi}.SetConfig`, params);
			loading = false;
			onSettingsResponse(res, device);
			cacheMode = selectedMode;
			cacheBrightness = brightnessValue;
			cacheBrightnessOn = brightnessOn;
			cacheBrightnessOff = brightnessOff;
			cacheColorOn = colorOn;
			cacheColorOff = colorOff;
		}
	};
	const splitColors = (color) => {
		return { r: color[0], g: color[1], b: color[2] };
	};
	const joinColors = (color) => {
		return [color.r, color.g, color.b];
	};
	const changeColor = (e, color) => {
		let value = joinColors(e.detail.color);
		switch (color) {
			case 'off':
				colorOff = value;
				break;
			case 'on':
				colorOn = value;
				break;

			default:
				break;
		}
	};

	const a11yPropsBrightnessValue = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightnessValue },
			default: `Current brightness value is ${brightnessValue}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightnessValue },
			default: `Current brightness value is ${brightnessValue}`,
		}),
	};

	const a11yPropsBrightnessOn = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightnessOn },
			default: `Current brightness value is ${brightnessOn}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightnessOn },
			default: `Current brightness value is ${brightnessOn}`,
		}),
	};

	const a11yPropsBrightnessOff = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightnessOff },
			default: `Current brightness value is ${brightnessOff}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightnessOff },
			default: `Current brightness value is ${brightnessOff}`,
		}),
	};
</script>

{#each visualModes as mode}
	<Radio
		size="m"
		class="mb-s"
		bind:selected={selectedMode}
		value={modes[visualModes.indexOf(mode)]}
		id="radio-y4thxj1"
	>
		{$format(`label_${mode}`)}
	</Radio>
{/each}

{#if selectedIndex == 0}
	{#if hasRelay}
		{#if $showNewLayout}
			<SliderV2
				id="slider-ytxwktl"
				a11yProps={a11yPropsBrightnessValue}
				class="mb-s"
				title={capFirstChar($format('collapse_title_brightness'))}
				type="horizontal"
				bind:value={brightnessValue}
				{min}
				{max}
			/>
		{:else}
			<Slider
				id="slider-ytxwktl"
				a11yProps={a11yPropsBrightnessValue}
				class="mb-s"
				title={capFirstChar($format('collapse_title_brightness'))}
				type="horizontal"
				bind:value={brightnessValue}
				{min}
				{max}
			/>
		{/if}
	{/if}
{:else if selectedIndex == 1}
	<Text styleType="body-small" class="mb-s"
		>{$format('title_led_ind_color_on', {
			default: 'Color when Plug is On',
		})}</Text
	>
	<ColorWheel
		id="colorwheel-j9gpv57"
		hideSaveBtn
		color={splitColors(colorOn)}
		on:color_change={(e) => changeColor(e, 'on')}
	/>
	{#if $showNewLayout}
		<SliderV2
			id="slider-5ftzgej"
			a11yProps={a11yPropsBrightnessOn}
			class="mb-s"
			title={capFirstChar($format('collapse_title_brightness'))}
			titleTypography="body-small"
			type="incremented"
			bind:value={brightnessOn}
			{min}
			{max}
			lineMarkers={[
				{ value: min, showLabel: true },
				{ value: max, showLabel: true },
			]}
		/>
	{:else}
		<Slider
			id="slider-5ftzgej"
			a11yProps={a11yPropsBrightnessOn}
			class="mb-s"
			title={capFirstChar($format('collapse_title_brightness'))}
			type="incremented"
			bind:value={brightnessOn}
			{min}
			{max}
		/>
	{/if}
	{#if colorOff}
		<!-- ToDo discuss if there will be one or two brightness sliders -->
		<Text styleType="body-small" class="mb-s"
			>{$format('title_led_ind_color_off', {
				default: 'Color when Plug is Off',
			})}</Text
		>
		<ColorWheel
			id="colorwheel-euzkltk"
			hideSaveBtn
			color={splitColors(colorOff)}
			on:color_change={(e) => changeColor(e, 'off')}
		/>
		{#if $showNewLayout}
			<SliderV2
				id="slider-d147mri"
				a11yProps={a11yPropsBrightnessOff}
				class="mb-s"
				title={capFirstChar($format('collapse_title_brightness'))}
				titleTypography="body-small"
				type="incremented"
				bind:value={brightnessOff}
				{min}
				{max}
				lineMarkers={[
					{ value: min, showLabel: true },
					{ value: max, showLabel: true },
				]}
			/>
		{:else}
			<Slider
				id="slider-d147mri"
				a11yProps={a11yPropsBrightnessOff}
				class="mb-s"
				title={capFirstChar($format('collapse_title_brightness'))}
				type="incremented"
				bind:value={brightnessOff}
				{min}
				{max}
			/>
		{/if}
	{/if}
{/if}

<Button {loading} width="full" size="m" on:click={setLedIndicator}>{$format('button_save')}</Button>
